import React from 'react';
import { useLocation, useNavigate, matchPath, Link } from 'react-router';
import { Bell, Search, Sparkles, User, LogOut, BellOff, Menu, Volume2, VolumeX } from 'lucide-react';
import { Button } from '../ui/button';
import { OPEN_PALETTE_EVENT } from './CommandPalette';
import { UserAvatar } from '../UserAvatar';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '../ui/dropdown-menu';
import { useAuth } from '../../hooks/useAuth';
import { useNotifications } from '../../hooks/useNotifications';
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip';
import {
  playNotificationSound,
  setSoundMuted,
  unlockAudio,
  useNotificationSound,
} from '../../lib/notificationAlerts';
import { SECTOR_NAMES } from '../../pages/board-completo/config';
import { usePageHeaderHost } from './PageHeader';

interface TopBarProps {
  onToggleAI: () => void;
  aiOpen: boolean;
  onMobileMenu?: () => void;
}

// ELE-137: route definitions ordered most-specific → least-specific.
// Declared at module level to avoid recreation on each render.
interface RouteDef {
  pattern: string;
  crumbs: readonly string[];
  /** Nome do param de rota cujo valor (mapeado via SECTOR_NAMES) é anexado como último crumb */
  dynamicParam?: string;
}

const ROUTE_DEFS: readonly RouteDef[] = [
  // sub-rotas pedagógico (antes das raízes)
  { pattern: '/pedagogico/alunos/:id',       crumbs: ['Home', 'Pedagógico', 'Alunos', 'Detalhe'] },
  { pattern: '/pedagogico/alunos',           crumbs: ['Home', 'Pedagógico', 'Alunos'] },
  { pattern: '/pedagogico/responsaveis/:id', crumbs: ['Home', 'Pedagógico', 'Responsáveis', 'Detalhe'] },
  { pattern: '/pedagogico/responsaveis',     crumbs: ['Home', 'Pedagógico', 'Responsáveis'] },
  { pattern: '/pedagogico/turmas/:id',       crumbs: ['Home', 'Pedagógico', 'Turmas', 'Detalhe'] },
  { pattern: '/pedagogico/turmas',           crumbs: ['Home', 'Pedagógico', 'Turmas'] },
  { pattern: '/pedagogico',                  crumbs: ['Home', 'Pedagógico'] },
  // triagem
  { pattern: '/board/triagem',               crumbs: ['Home', 'Triagem Central'] },
  // board individual (rota real usada por BoardCompleto: /board/:sector)
  { pattern: '/board/:sector',               crumbs: ['Home', 'Boards'], dynamicParam: 'sector' },
  // boards (listagem)
  { pattern: '/boards/setor/:key',           crumbs: ['Home', 'Boards', 'Board do Setor'] },
  { pattern: '/boards/:id',                  crumbs: ['Home', 'Boards', 'Board'] },
  { pattern: '/boards',                      crumbs: ['Home', 'Boards'] },
  // secretaria
  { pattern: '/',                            crumbs: ['Home', 'Secretaria'] },
  // perfil
  { pattern: '/profile',                     crumbs: ['Home', 'Perfil'] },
  { pattern: '/notificacoes',                crumbs: ['Home', 'Notificações'] },
  { pattern: '/school/profile',              crumbs: ['Home', 'Escola'] },
  // agente
  { pattern: '/agent-config',                crumbs: ['Home', 'Configuração do Agente'] },
  { pattern: '/faqs',                        crumbs: ['Home', 'FAQs'] },
  // fallback
  { pattern: '*',                            crumbs: ['Home'] },
];

function timeAgo(dateStr: string): string {
  const secs = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (secs < 60) return 'agora';
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `há ${mins} min`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `há ${hours}h`;
  return `há ${Math.floor(hours / 24)}d`;
}

export default function TopBar({ onToggleAI, aiOpen, onMobileMenu }: TopBarProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { unreadCount, items, loading, resolvingId, fetchList, markAllRead, openNotification } = useNotifications();
  const pageHeader = usePageHeaderHost();

  // ELE-137: dynamic breadcrumb using matchPath
  const matched = ROUTE_DEFS.find(r => matchPath(r.pattern, location.pathname));
  let breadcrumbs: readonly string[] = matched?.crumbs ?? ['Home'];

  if (matched?.dynamicParam) {
    const m = matchPath(matched.pattern, location.pathname);
    const raw = m?.params[matched.dynamicParam];
    if (raw) {
      breadcrumbs = [...matched.crumbs, SECTOR_NAMES[raw] ?? raw];
    }
  }

  const fullName = user
    ? (`${user.first_name} ${user.last_name}`.trim() || user.username)
    : '?';

  const roleLabels: Record<string, string> = {
    manager: 'Gestor',
    coordinator: 'Coordenador',
    operator: 'Operador',
    end_user: 'Usuário Final',
  };
  const roleLabel = user?.profile?.role
    ? (roleLabels[user.profile.role] ?? 'Usuário')
    : 'Usuário';

  const handleLogout = async () => {
    try {
      await logout();
    } catch {
      // ignore logout errors — navigate will still happen in finally
    } finally {
      navigate('/login');
    }
  };

  return (
    <header data-app-topbar className="h-11 lg:h-13 bg-white dark:bg-[#0a0b0d] border-b border-slate-100 dark:border-white/[0.06] flex items-center justify-between gap-4 px-3 lg:px-5 flex-shrink-0">
      <div className="flex items-center gap-2 min-w-0 flex-1 h-full">
        {/* Mobile hamburger */}
        {onMobileMenu && (
          <button
            onClick={onMobileMenu}
            className="md:hidden size-8 flex items-center justify-center rounded-md text-slate-500 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-white/[0.06] transition-colors"
          >
            <Menu className="size-5" />
          </button>
        )}
      {/* Breadcrumb — só quando a página não pôs o próprio título aqui (PageHeader). */}
      {!pageHeader?.active && (
        <nav className="flex items-center gap-1.5 text-sm">
          {breadcrumbs.map((crumb, i) => (
            <React.Fragment key={i}>
              {i > 0 && <span className="text-slate-200 dark:text-slate-700">/</span>}
              <span className={i === breadcrumbs.length - 1 ? 'text-slate-800 dark:text-slate-100 font-medium' : 'text-slate-400 dark:text-slate-500'}>
                {crumb}
              </span>
            </React.Fragment>
          ))}
        </nav>
      )}
      <div ref={pageHeader?.setSlot} className="contents" />
      </div>

      {/* Right Actions */}
      <div className="flex items-center gap-2 flex-shrink-0">
        {/* Abre o Ctrl+K (CommandPalette) — busca contatos, cards, páginas e ações. */}
        <button
          type="button"
          data-testid="open-command-palette"
          onClick={() => window.dispatchEvent(new Event(OPEN_PALETTE_EVENT))}
          className="hidden lg:flex items-center gap-2 pl-2.5 pr-2 w-56 h-8 rounded-md border border-slate-100 bg-slate-50 text-sm text-slate-400 hover:bg-white hover:border-slate-200 dark:bg-white/[0.03] dark:border-white/[0.08] transition-colors"
        >
          <Search className="size-3.5 text-slate-300 dark:text-slate-600" />
          <span className="flex-1 text-left">Buscar...</span>
          <kbd className="text-[10px] font-medium text-slate-400 border border-slate-200 rounded px-1">Ctrl K</kbd>
        </button>

        {/* AI Co-pilot Button — oculto temporariamente, feature ainda não pronta. Não remover. */}
        {false && (
          <Button
            variant={aiOpen ? 'default' : 'outline'}
            size="sm"
            onClick={onToggleAI}
            className={`gap-1.5 h-8 text-xs font-medium transition-all ${
              aiOpen
                ? 'bg-indigo-600 hover:bg-indigo-700 text-white border-indigo-600'
                : 'border-indigo-200 text-indigo-600 hover:bg-indigo-50'
            }`}
          >
            <Sparkles className="size-3.5" />
            Co-piloto IA
            <span className="size-1.5 rounded-full bg-emerald-400 animate-pulse" />
          </Button>
        )}

        <NotificationSoundButton />

        {/* ELE-140 Fase 2: sino ligado a GET /notifications/ real */}
        <DropdownMenu onOpenChange={(open) => { if (open) fetchList(); }}>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="relative size-8">
              <Bell className="size-4 text-slate-500 dark:text-slate-400" />
              {unreadCount > 0 && (
                <span className="absolute top-0.5 right-0.5 min-w-3.5 h-3.5 px-0.5 rounded-full bg-red-500 text-white text-[9px] font-semibold flex items-center justify-center leading-none">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-80" align="end">
            {/* Fixed header */}
            <div className="flex items-center justify-between px-3 py-2.5 border-b border-slate-100 dark:border-white/[0.08]">
              <span className="text-sm font-semibold text-slate-800 dark:text-slate-100">Notificações</span>
              {unreadCount > 0 ? (
                <button
                  type="button"
                  onClick={() => markAllRead()}
                  className="text-xs text-indigo-600 hover:text-indigo-700 dark:text-indigo-400 dark:hover:text-indigo-300 font-medium"
                >
                  Marcar todas como lidas
                </button>
              ) : (
                <span className="text-xs text-slate-400 dark:text-slate-500">0 não lidas</span>
              )}
            </div>

            {loading && items.length === 0 && (
              <div className="py-2 px-3 space-y-2">
                {[0, 1, 2].map((i) => (
                  <div key={i} className="animate-pulse space-y-1.5 py-1.5">
                    <div className="h-3 bg-slate-100 dark:bg-white/10 rounded w-3/4" />
                    <div className="h-2.5 bg-slate-100 dark:bg-white/10 rounded w-1/2" />
                  </div>
                ))}
              </div>
            )}

            {!loading && items.length === 0 && (
              <div className="flex flex-col items-center justify-center py-8 px-4 text-center">
                <BellOff className="size-8 text-slate-300 dark:text-slate-700 mb-3" />
                <p className="text-sm font-medium text-slate-600 dark:text-slate-300">Nenhuma notificação</p>
                <p className="text-xs text-slate-400 dark:text-slate-500 mt-1 leading-relaxed">
                  Alertas de cards e atividades aparecerão aqui
                </p>
              </div>
            )}

            {items.length > 0 && (
              <div className="max-h-80 overflow-y-auto">
                {items.map((n) => (
                  <button
                    key={n.id}
                    type="button"
                    disabled={resolvingId === n.id}
                    onClick={() => openNotification(n)}
                    className={`w-full text-left px-3 py-2.5 border-b border-slate-50 dark:border-white/[0.05] last:border-b-0 hover:bg-slate-50 dark:hover:bg-white/[0.06] transition-colors disabled:opacity-60 ${
                      n.is_read ? '' : 'bg-indigo-50/40 dark:bg-indigo-500/10'
                    }`}
                  >
                    <div className="flex items-start gap-2">
                      {!n.is_read && (
                        <span className="mt-1.5 size-1.5 rounded-full bg-indigo-500 flex-shrink-0" />
                      )}
                      <div className={`flex-1 min-w-0 ${n.is_read ? 'pl-3.5' : ''}`}>
                        <p className={`text-xs leading-snug ${n.is_read ? 'text-slate-500 dark:text-slate-400' : 'text-slate-800 dark:text-slate-100 font-medium'}`}>
                          {n.title}
                        </p>
                        {n.body && (
                          <p className="text-[11px] text-slate-400 dark:text-slate-500 mt-0.5 leading-snug line-clamp-2">
                            {n.body}
                          </p>
                        )}
                        <p className="text-[10px] text-slate-300 dark:text-slate-600 mt-1">{timeAgo(n.created_at)}</p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}

            <Link
              to="/notificacoes"
              className="block text-center text-xs font-medium text-indigo-600 hover:text-indigo-700 dark:text-indigo-400 dark:hover:text-indigo-300 py-2.5 border-t border-slate-100 dark:border-white/[0.08]"
            >
              Ver todas as notificações
            </Link>
          </DropdownMenuContent>
        </DropdownMenu>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="rounded-full outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
            >
              <UserAvatar
                name={fullName}
                avatarUrl={user?.profile?.avatar_url}
                className="size-8 ring-1 ring-slate-100 dark:ring-white/[0.08] cursor-pointer"
                fallbackClassName="bg-gradient-to-br from-indigo-600 to-violet-600 text-white text-xs font-semibold"
              />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-56" align="end">
            {/* User info header — non-interactive */}
            <div className="px-2 py-2 space-y-0.5">
              <p className="text-sm font-medium text-slate-900 dark:text-slate-100 leading-none">
                {user
                  ? (`${user.first_name} ${user.last_name}`.trim() || user.username)
                  : '—'}
              </p>
              <p className="text-xs text-slate-400 dark:text-slate-500 leading-none mt-1">{user?.email}</p>
              <span className="inline-block mt-1.5 text-[10px] font-medium text-indigo-600 bg-indigo-50 dark:text-indigo-300 dark:bg-indigo-500/15 px-1.5 py-0.5 rounded">
                {roleLabel}
              </span>
            </div>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => navigate('/profile')}>
              <User className="mr-2 size-4" />
              Meu Perfil
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={handleLogout}
              className="text-red-600 focus:text-red-600 focus:bg-red-50"
            >
              <LogOut className="mr-2 size-4" />
              Sair
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}

/**
 * Liga/desliga o som das notificações (ver notificationAlerts.ts). Enquanto o
 * navegador ainda não liberou áudio (nenhum clique na página desde que abriu),
 * mostra um ponto âmbar — clicar destrava e toca uma amostra.
 */
function NotificationSoundButton() {
  const { muted, blocked } = useNotificationSound();

  const handleClick = async () => {
    if (blocked) {
      const ok = await unlockAudio();
      if (ok && !muted) playNotificationSound('normal');
      return;
    }
    if (muted) {
      setSoundMuted(false);
      playNotificationSound('normal', { force: true });
    } else {
      setSoundMuted(true);
    }
  };

  const label = muted
    ? 'Som das notificações desligado — clique para ligar'
    : blocked
      ? 'Clique para ativar o som das notificações'
      : 'Som das notificações ligado — clique para silenciar';

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative size-8"
          onClick={handleClick}
          aria-label={label}
          aria-pressed={!muted}
        >
          {muted ? (
            <VolumeX className="size-4 text-slate-400 dark:text-slate-500" />
          ) : (
            <Volume2 className="size-4 text-slate-500 dark:text-slate-400" />
          )}
          {!muted && blocked && (
            <span className="absolute top-1 right-1 size-2 rounded-full bg-amber-500" />
          )}
        </Button>
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  );
}
